import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Link as MLink,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import type { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { documentSourceLabel } from 'src/pages/Receptors/documentSourceLabel.helpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFileLines,
  faFolder,
  faHardDrive,
} from '@fortawesome/free-regular-svg-icons'
import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
import { SearchIcon } from 'src/components/Reusable/Search/Search.styles'
import { ClipboardButton } from 'src/components/Reusable/Buttons/ClipboardButton/ClipboardButton'
import { SelectDropdown } from 'src/components/Reusable/SelectDropdown/SelectDropdown'
import { FilterTabs } from 'src/components/Reusable/Tabs/FilterTabs'
import { GridColumnSortType } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import { getColSortIcon } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.utils'
import type { NodeNotice } from './DocumentPicker.providers'
import type { PickerProblem } from './DocumentPicker.hooks'
import type { DocumentNode } from './DocumentPicker.types'
import { isContainerLike } from './DocumentPicker.types'
import {
  BreadcrumbCurrentSx,
  BreadcrumbLinkSx,
  BreadcrumbsSx,
  EmptyState,
  ResultsTable,
  RightPaneLoading,
  SearchContainer,
  SearchInput,
  SuggestionsPopover,
  TableHeadRow,
  TablePaneContainer,
  TdCheckbox,
  TdModified,
  TdName,
  Th,
  ThCheckbox,
  ThModified,
  Tr,
  TrSelected,
  TreePaneContainer,
  TreePaneList,
} from './DocumentPicker.styles'

/**
 * Presentational pieces of the document picker. They render what the tree
 * state holds and report clicks; loading is the modal's job.
 */

const truncate = (input: string | null | undefined, maxLen = 60): string => {
  const s = (input ?? '').trim()
  if (s.length <= maxLen) return s
  return `${s.slice(0, Math.max(0, maxLen - 1))}…`
}

/**
 * Hover tooltip for a node name that may be truncated: the full name, a
 * copy button, and an optional note. Interactive so the pointer can reach
 * the button.
 */
const NameTooltip = ({
  name,
  note,
  children,
}: {
  name: string
  note?: string
  children: React.ReactElement
}): JSX.Element => (
  <Tooltip
    title={
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
          {note ? `${name} — ${note}` : name}
        </Box>
        <ClipboardButton text={name} placement="bottom" light />
      </Stack>
    }
    placement="top"
    arrow
    enterDelay={300}
    leaveDelay={150}
  >
    {children}
  </Tooltip>
)

/** Info icon with the notice a provider attached to a node, if any. */
const NodeNoticeIcon = ({
  notice,
}: {
  notice: NodeNotice | undefined
}): JSX.Element | null => {
  if (!notice) return null
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="body2">{notice.summary}</Typography>
          {notice.command && (
            <Box
              component="pre"
              sx={{
                m: 0,
                mt: 1,
                fontSize: 11,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                userSelect: 'all',
              }}
            >
              {notice.command}
            </Box>
          )}
        </Box>
      }
      placement="right"
      arrow
    >
      <Box
        component="span"
        role="img"
        aria-label="More information"
        onClick={(e) => e.stopPropagation()}
        sx={{ display: 'inline-flex', color: 'text.secondary', cursor: 'help' }}
      >
        <FontAwesomeIcon icon={faCircleInfo} style={{ fontSize: 13 }} />
      </Box>
    </Tooltip>
  )
}

/** Columns the listing can be sorted on. */
export type ListSortColumn = 'name' | 'modified'

export interface ListSort {
  col: ListSortColumn
  type: GridColumnSortType
  onSort: (col: ListSortColumn) => void
}

const byTitle = (a: DocumentNode, b: DocumentNode): number =>
  a.title.localeCompare(b.title, undefined, { sensitivity: 'base' })

/** Alphabetical by name, case-insensitive: the tree's only order. */
export const sortByTitle = (nodes: DocumentNode[]): DocumentNode[] =>
  [...nodes].sort(byTitle)

const modifiedTime = (node: DocumentNode): number => {
  if (!node.modifiedAt) return NaN
  const asNum = Number(node.modifiedAt)
  const d = Number.isFinite(asNum)
    ? new Date(asNum > 1e12 ? asNum : asNum * 1000)
    : new Date(node.modifiedAt)
  return d.getTime()
}

/**
 * The listing in the chosen order. Nodes without a date sort last whichever
 * way the date column goes, so they never hide the dated ones.
 */
export const sortNodes = (
  nodes: DocumentNode[],
  col: ListSortColumn,
  type: GridColumnSortType,
): DocumentNode[] => {
  const dir = type === GridColumnSortType.DESCENDING ? -1 : 1
  return [...nodes].sort((a, b) => {
    if (col === 'modified') {
      const ta = modifiedTime(a)
      const tb = modifiedTime(b)
      const aMissing = Number.isNaN(ta)
      const bMissing = Number.isNaN(tb)
      if (aMissing !== bMissing) return aMissing ? 1 : -1
      if (!aMissing && ta !== tb) return (ta - tb) * dir
    }
    return byTitle(a, b) * dir
  })
}

/** The app's column sort control: the icon states and sizing of the grids. */
const SortByIcon = ({
  label,
  active,
  type,
  onClick,
}: {
  label: string
  active: boolean
  type: GridColumnSortType
  onClick: () => void
}): JSX.Element => (
  <Box
    component="span"
    role="button"
    aria-label={`Sort by the ${label} column`}
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      width: 26,
      justifyContent: 'center',
      alignItems: 'center',
      verticalAlign: 'middle',
      cursor: 'pointer',
      '& svg': { height: 19, width: 11 },
    }}
  >
    {getColSortIcon(type, active)}
  </Box>
)

const nodeIcon = (node: DocumentNode) =>
  node.kind === 'container'
    ? faHardDrive
    : isContainerLike(node)
    ? faFolder
    : faFileLines

export const formatModified = (raw: string | undefined): string => {
  if (!raw) return '—'
  const asNum = Number(raw)
  const d = Number.isFinite(asNum)
    ? new Date(asNum > 1e12 ? asNum : asNum * 1000)
    : new Date(raw)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ---------------------------------------------------------------------------
// Tree (left pane)
// ---------------------------------------------------------------------------

export interface TreePaneProps {
  roots: DocumentNode[]
  childrenOf: (parentId: string) => DocumentNode[]
  isExpandable: (node: DocumentNode) => boolean
  isLoading: (parentId: string) => boolean
  expandedIds: Set<string>
  currentId: string | null
  onSelect: (node: DocumentNode) => void
  onToggle: (node: DocumentNode) => void
  /** A provider's notice for a node, shown behind an info icon. */
  notice?: (node: DocumentNode) => NodeNotice | undefined
}

export const TreePane = ({
  roots,
  childrenOf,
  isExpandable,
  isLoading,
  expandedIds,
  currentId,
  onSelect,
  onToggle,
  notice,
}: TreePaneProps): JSX.Element => {
  const renderNode = (node: DocumentNode, depth: number): React.ReactNode => {
    if (!isContainerLike(node)) return null
    const expanded = expandedIds.has(node.id)
    const expandable = isExpandable(node)
    const loading = isLoading(node.id)
    const kids = expanded
      ? sortByTitle(childrenOf(node.id).filter(isContainerLike))
      : []

    return (
      <React.Fragment key={node.id}>
        <ListItemButton
          selected={currentId === node.id}
          onClick={() => onSelect(node)}
          sx={{
            pl: 1 + depth * 2,
            borderRadius: 1,
            ...(depth > 0
              ? { borderLeft: '1px solid', borderColor: 'divider', ml: 0.5 }
              : {}),
          }}
        >
          <ListItemText
            primary={
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  component="span"
                  role={expandable ? 'button' : undefined}
                  aria-label={
                    expandable ? (expanded ? 'Collapse' : 'Expand') : undefined
                  }
                  onClick={(e) => {
                    if (!expandable) return
                    e.stopPropagation()
                    onToggle(node)
                  }}
                  sx={{
                    width: 16,
                    minWidth: 16,
                    display: 'inline-flex',
                    justifyContent: 'center',
                    fontFamily: 'monospace',
                    color: expandable ? 'text.secondary' : 'transparent',
                    userSelect: 'none',
                    cursor: expandable ? 'pointer' : 'default',
                  }}
                >
                  {loading ? (
                    <CircularProgress size={10} />
                  ) : expandable ? (
                    expanded ? (
                      '▾'
                    ) : (
                      '▸'
                    )
                  ) : (
                    '·'
                  )}
                </Box>
                <FontAwesomeIcon
                  icon={nodeIcon(node)}
                  style={{ fontSize: 13 }}
                />
                <NameTooltip name={node.title}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{ fontWeight: 500, minWidth: 0 }}
                  >
                    {node.title}
                  </Typography>
                </NameTooltip>
                <NodeNoticeIcon notice={notice?.(node)} />
              </Stack>
            }
          />
        </ListItemButton>
        {expanded && kids.map((c) => renderNode(c, depth + 1))}
      </React.Fragment>
    )
  }

  return (
    <TreePaneContainer>
      <TreePaneList dense>
        {sortByTitle(roots).map((r) => renderNode(r, 0))}
      </TreePaneList>
    </TreePaneContainer>
  )
}

// ---------------------------------------------------------------------------
// Listing (right pane)
// ---------------------------------------------------------------------------

export interface ListPaneProps {
  nodes: DocumentNode[]
  loading: boolean
  hasMore: boolean
  loadingMore: boolean
  onLoadMore: () => void
  selectedIds: Set<string>
  /** Which nodes carry a checkbox. */
  isSelectable: (node: DocumentNode) => boolean
  onToggleSelected: (node: DocumentNode) => void
  onOpen: (node: DocumentNode) => void
  emptyText: string
  /** A provider's notice for a node, shown behind an info icon. */
  notice?: (node: DocumentNode) => NodeNotice | undefined
  /** Column sort controls in the header; the caller orders `nodes`. */
  sort?: ListSort
}

export const ListPane = ({
  nodes,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  selectedIds,
  isSelectable,
  onToggleSelected,
  onOpen,
  emptyText,
  notice,
  sort,
}: ListPaneProps): JSX.Element => {
  if (loading) {
    return (
      <RightPaneLoading>
        <CircularProgress />
      </RightPaneLoading>
    )
  }

  return (
    <TablePaneContainer>
      <ResultsTable>
        <thead>
          <TableHeadRow>
            <ThCheckbox />
            <Th>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary' }}
              >
                Name
              </Typography>
              {sort && (
                <SortByIcon
                  label="Name"
                  active={sort.col === 'name'}
                  type={sort.type}
                  onClick={() => sort.onSort('name')}
                />
              )}
            </Th>
            <ThModified>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.secondary' }}
              >
                Last Updated At
              </Typography>
              {sort && (
                <SortByIcon
                  label="Last Updated At"
                  active={sort.col === 'modified'}
                  type={sort.type}
                  onClick={() => sort.onSort('modified')}
                />
              )}
            </ThModified>
          </TableHeadRow>
        </thead>
        <tbody>
          {nodes.map((node) => {
            const openable = isContainerLike(node)
            const selectable = isSelectable(node)
            const selected = selectedIds.has(node.id)
            // Shown for context only: a file while picking a folder.
            const inert = !openable && !selectable
            const Row = selected ? TrSelected : Tr
            return (
              <Row
                key={node.id}
                onClick={() => {
                  if (openable) onOpen(node)
                  else if (selectable) onToggleSelected(node)
                }}
                style={inert ? { opacity: 0.55, cursor: 'default' } : undefined}
                aria-disabled={inert || undefined}
              >
                <TdCheckbox>
                  {selectable && (
                    <Checkbox
                      size="small"
                      checked={selected}
                      onClick={(e) => {
                        e.stopPropagation()
                        onToggleSelected(node)
                      }}
                    />
                  )}
                </TdCheckbox>
                <TdName>
                  <NameTooltip
                    name={node.title}
                    note={
                      inert
                        ? 'files are collected by the scan; pick a folder'
                        : undefined
                    }
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ minWidth: 0 }}
                    >
                      <FontAwesomeIcon
                        icon={nodeIcon(node)}
                        style={{ fontSize: 16, flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {truncate(node.title)}
                      </Typography>
                      <NodeNoticeIcon notice={notice?.(node)} />
                    </Stack>
                  </NameTooltip>
                </TdName>
                <TdModified>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {formatModified(node.modifiedAt)}
                  </Typography>
                </TdModified>
              </Row>
            )
          })}
        </tbody>
      </ResultsTable>

      {nodes.length === 0 && (
        <EmptyState>
          <Typography variant="body2">{emptyText}</Typography>
        </EmptyState>
      )}

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
          <Button size="small" onClick={onLoadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </TablePaneContainer>
  )
}

// ---------------------------------------------------------------------------
// Breadcrumbs
// ---------------------------------------------------------------------------

export const PathBreadcrumbs = ({
  path,
  rootCaption,
  onJump,
  onJumpRoot,
}: {
  path: DocumentNode[]
  rootCaption: string
  onJump: (node: DocumentNode) => void
  onJumpRoot: () => void
}): JSX.Element => {
  const current = path[path.length - 1]
  const parentNode = path.length > 1 ? path[path.length - 2] : null
  const showEllipsis = path.length > 3
  const middle = !showEllipsis && path.length === 3 ? path[1] : null
  const root = path.length > 0 ? path[0] : null

  return (
    <Breadcrumbs aria-label="breadcrumb" sx={BreadcrumbsSx}>
      {current ? (
        <MLink
          underline="hover"
          component="button"
          color="primary"
          onClick={onJumpRoot}
          sx={BreadcrumbLinkSx}
        >
          {rootCaption}
        </MLink>
      ) : (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={BreadcrumbCurrentSx}
        >
          {rootCaption}
        </Typography>
      )}
      {root && root.id !== current?.id && (
        <MLink
          underline="hover"
          component="button"
          color="text.primary"
          onClick={() => onJump(root)}
          sx={BreadcrumbLinkSx}
        >
          {root.title}
        </MLink>
      )}
      {showEllipsis && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ lineHeight: 1.2 }}
        >
          …
        </Typography>
      )}
      {middle && (
        <MLink
          underline="hover"
          component="button"
          color="text.primary"
          onClick={() => onJump(middle)}
          sx={BreadcrumbLinkSx}
        >
          {middle.title}
        </MLink>
      )}
      {showEllipsis && parentNode && parentNode.id !== root?.id && (
        <MLink
          underline="hover"
          component="button"
          color="text.primary"
          onClick={() => onJump(parentNode)}
          sx={BreadcrumbLinkSx}
        >
          {parentNode.title}
        </MLink>
      )}
      {current && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={BreadcrumbCurrentSx}
        >
          {current.title}
        </Typography>
      )}
    </Breadcrumbs>
  )
}

// ---------------------------------------------------------------------------
// Search box: server search when the source supports it, else a filter over
// what has been loaded. Either way results are a list to jump to.
// ---------------------------------------------------------------------------

export interface SearchBoxProps {
  serverSearch: boolean
  searching: boolean
  /** Server results when serverSearch, else the loaded nodes to filter. */
  candidates: DocumentNode[]
  onSearch: (term: string) => void
  onPick: (node: DocumentNode) => void
  placeholder: string
}

const scoreTitle = (title: string, q: string): number => {
  const t = title.toLowerCase()
  const idx = t.indexOf(q)
  if (idx >= 0) return 1000 - idx * 5 - Math.min(t.length, 200)
  let ti = 0
  let matched = 0
  for (const ch of q) {
    while (ti < t.length && t[ti] !== ch) ti++
    if (ti < t.length && t[ti] === ch) {
      matched++
      ti++
    } else break
  }
  return matched === 0 ? 0 : matched * 10 - Math.min(t.length, 200) / 50
}

export const SearchBox = ({
  serverSearch,
  searching,
  candidates,
  onSearch,
  onPick,
  placeholder,
}: SearchBoxProps): JSX.Element => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [highlight, setHighlight] = useState(-1)

  // Server search is debounced and only sent when the term changed; the
  // local filter is instant.
  const lastSentRef = React.useRef('')
  useEffect(() => {
    if (!serverSearch) return
    const term = query.trim()
    if (term === lastSentRef.current) return
    const handle = window.setTimeout(() => {
      lastSentRef.current = term
      onSearch(term)
    }, 350)
    return () => window.clearTimeout(handle)
  }, [query, serverSearch, onSearch])

  const results = useMemo<DocumentNode[]>(() => {
    const q = query.trim().toLowerCase()
    if (!q) return []
    if (serverSearch) return candidates.slice(0, 8)
    return candidates
      .map((n) => ({ n, score: scoreTitle(n.title || '', q) }))
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map((r) => r.n)
  }, [query, serverSearch, candidates])

  const pick = (node: DocumentNode): void => {
    setOpen(false)
    setHighlight(-1)
    setQuery('')
    if (serverSearch) {
      lastSentRef.current = ''
      onSearch('')
    }
    onPick(node)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') {
      setOpen(false)
      setHighlight(-1)
      return
    }
    if (e.key === 'ArrowDown') {
      setOpen(true)
      setHighlight((h) => Math.min(h + 1, results.length - 1))
      e.preventDefault()
      return
    }
    if (e.key === 'ArrowUp') {
      setHighlight((h) => Math.max(h - 1, 0))
      e.preventDefault()
      return
    }
    if (e.key === 'Enter') {
      const r = results[highlight >= 0 ? highlight : 0]
      if (r) {
        pick(r)
        e.preventDefault()
      }
    }
  }

  return (
    <SearchContainer>
      <SearchIcon />
      <SearchInput
        value={query}
        placeholder={placeholder}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          setQuery(e.target.value)
          setOpen(true)
          setHighlight(-1)
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
      />
      {open && query.trim() && (searching || results.length > 0) && (
        <SuggestionsPopover>
          <List dense sx={{ p: 0 }}>
            {searching && results.length === 0 && (
              <ListItemButton disabled>
                <ListItemText
                  primary={<Typography variant="body2">Searching…</Typography>}
                />
              </ListItemButton>
            )}
            {results.map((node, idx) => (
              <ListItemButton
                key={node.id}
                selected={idx === highlight}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(node)}
              >
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FontAwesomeIcon
                        icon={nodeIcon(node)}
                        style={{ fontSize: 12 }}
                      />
                      <Typography variant="body2" noWrap>
                        {node.title}
                      </Typography>
                    </Stack>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        </SuggestionsPopover>
      )}
    </SearchContainer>
  )
}

// ---------------------------------------------------------------------------
// Status banner: what the provider said, in the user's terms.
// ---------------------------------------------------------------------------

export const ProblemBanner = ({
  problem,
  vendorLabel,
  identity,
  onRetry,
  onEditCredentials,
}: {
  problem: PickerProblem
  vendorLabel: string
  identity?: string
  onRetry?: () => void
  onEditCredentials?: () => void
}): JSX.Element => {
  const [secondsLeft, setSecondsLeft] = useState<number | null>(
    problem.kind === 'rateLimit' && problem.retryAfterSeconds
      ? problem.retryAfterSeconds
      : null,
  )
  const retriedRef = React.useRef(false)

  useEffect(() => {
    if (secondsLeft === null) return
    if (secondsLeft <= 0) {
      if (!retriedRef.current) {
        retriedRef.current = true
        onRetry?.()
      }
      return
    }
    const handle = window.setTimeout(
      () => setSecondsLeft(secondsLeft - 1),
      1000,
    )
    return () => window.clearTimeout(handle)
  }, [secondsLeft, onRetry])

  switch (problem.kind) {
    case 'rateLimit':
      return (
        <Alert
          severity="warning"
          action={
            onRetry && (
              <Button size="small" onClick={onRetry}>
                Retry now
              </Button>
            )
          }
        >
          {vendorLabel} is rate limiting requests.
          {secondsLeft !== null && secondsLeft > 0
            ? ` Retrying in ${secondsLeft}s.`
            : ' Try again in a moment.'}
        </Alert>
      )
    case 'credentials':
      return (
        <Alert
          severity="error"
          action={
            onEditCredentials && (
              <Button size="small" onClick={onEditCredentials}>
                Edit credentials
              </Button>
            )
          }
        >
          {vendorLabel} rejected this connection’s credentials. Re-authorize it
          to keep browsing.
        </Alert>
      )
    case 'noAccess':
      return (
        <Alert severity="info">
          {identity
            ? `${identity} has no access to this location.`
            : 'The connection has no access to this location.'}
          {identity ? ` Share it with ${identity} to see it here.` : ''}
        </Alert>
      )
    case 'notFound':
      return (
        <Alert severity="info">
          This location no longer exists in {vendorLabel}.
        </Alert>
      )
    case 'unavailable':
      return (
        <Alert
          severity="warning"
          action={
            onRetry && (
              <Button size="small" onClick={onRetry}>
                Retry
              </Button>
            )
          }
        >
          {vendorLabel} did not answer in time.
        </Alert>
      )
    default:
      return (
        <Alert
          severity="error"
          action={
            onRetry && (
              <Button size="small" onClick={onRetry}>
                Retry
              </Button>
            )
          }
        >
          Could not load from {vendorLabel}: {problem.message}
        </Alert>
      )
  }
}

/**
 * Which connection the picker browses, when the account has several of the
 * same document receptor (two Google Drive service accounts, say). Renders
 * nothing with one, so the single-connection picker is unchanged.
 */
/** Browse the hierarchy (spaces, folders) or the provider's labels. */
export const ViewToggle = ({
  value,
  treeLabel,
  onChange,
}: {
  value: 'tree' | 'labels'
  /** What the hierarchy's top level is called, e.g. "Spaces". */
  treeLabel: string
  onChange: (next: 'tree' | 'labels') => void
}): JSX.Element => (
  <Stack direction="row" alignItems="center" spacing={1}>
    <Typography variant="caption" color="text.secondary">
      Browse by
    </Typography>
    <FilterTabs
      options={[treeLabel, 'Labels']}
      currentSelected={value === 'labels' ? 'Labels' : treeLabel}
      onSelect={(tab) => onChange(tab === 'Labels' ? 'labels' : 'tree')}
      size="small"
    />
  </Stack>
)

/**
 * Which connection of the model is browsed, as a dropdown sitting in the
 * modal title ("Add Google Drive evidence [gd2 v]"); nothing when the
 * account has one. Sized to the longest connection name so switching does
 * not reflow the title.
 */
export const ConnectionSelect = ({
  receptors,
  value,
  onChange,
}: {
  receptors: ReceptorRecord[]
  /** Id of the connection being browsed. */
  value: string
  onChange: (receptor: ReceptorRecord) => void
}): JSX.Element | null => {
  const items = useMemo(
    () =>
      receptors.map((receptor) => ({
        name: documentSourceLabel(receptor),
        value: receptor.getId(),
      })),
    [receptors],
  )
  const selected = useMemo(
    () => items.filter((item) => item.value === value),
    [items, value],
  )
  const longest = useMemo(
    () => items.reduce((n, item) => Math.max(n, item.name.length), 0),
    [items],
  )
  if (receptors.length < 2) return null
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        verticalAlign: 'middle',
        ml: 1,
        // The name plus the chevron and padding, in characters.
        width: `${Math.min(Math.max(longest, 6) + 5, 48)}ch`,
        fontSize: '1rem',
        fontWeight: 400,
      }}
    >
      <SelectDropdown
        label="Connection"
        hideLabel
        items={items}
        defaultValues={selected}
        onSelectCb={(picked) => {
          const next = receptors.find(
            (receptor) => receptor.getId() === picked[0]?.value,
          )
          if (next) onChange(next)
        }}
        hideError
      />
    </Box>
  )
}
